import { useState, useEffect, useRef } from 'react'
import { Code, Plus, Minus } from '@phosphor-icons/react'
import { workflowsApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { TextShimmer } from '@/components/ui/TextShimmer'
import { Tooltip } from '@/components/ui/Tooltip'

interface VariableGroup {
  field: string
  label: string
  token: string
}

interface VariableInsertButtonProps {
  onInsert: (token: string) => void
}

const NAMESPACE_LABELS: Record<string, string> = {
  contact: 'Contact',
  sender: 'Sender',
  account: 'Account',
  enrichment_pdl: 'People Data Labs',
  enrichment_explorium: 'Explorium',
  enrichment_native: 'Native AI',
  enrichment_custom: 'Custom AI',
}

export default function VariableInsertButton({ onInsert }: VariableInsertButtonProps) {
  const [open, setOpen] = useState(false)
  const [variables, setVariables] = useState<Record<string, VariableGroup[]> | null>(null)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open && !variables) {
      workflowsApi.getTemplateVariables().then(res => setVariables(res.data)).catch(() => {})
    }
  }, [open, variables])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const toggle = (ns: string) => setCollapsed(prev => ({ ...prev, [ns]: !prev[ns] }))

  return (
    <div className="relative inline-block" ref={ref}>
      <Tooltip label="Insert variable">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn(
            'inline-flex items-center gap-1 px-2 h-8 rounded-md border transition-colors duration-150',
            open
              ? 'bg-[#5e6ad2]/[0.10] border-[#5e6ad2]/30 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/35 dark:text-[#a5b4fc]'
              : 'border-border/60 text-muted-foreground hover:border-[#5e6ad2]/30 hover:text-[#5e6ad2] hover:bg-[#5e6ad2]/[0.06] dark:border-white/[0.08] dark:text-[#8a8f98] dark:hover:border-[#5e6ad2]/35 dark:hover:text-[#a5b4fc] dark:hover:bg-[#5e6ad2]/[0.10]',
          )}
          style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
          aria-label="Insert variable"
        >
          <Code className="w-3.5 h-3.5" weight="bold" />
          {'{x}'}
        </button>
      </Tooltip>
      {open && (
        <div
          className="absolute right-0 left-auto z-50 mt-1 w-80 max-h-80 overflow-y-auto bg-card dark:bg-[#191a1b] border border-border/60 dark:border-white/[0.08] rounded-lg shadow-lg dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)] p-1"
        >
          {variables ? (
            Object.entries(variables).map(([ns, vars]) => (
              <div key={ns} className="mb-1 last:mb-0">
                <button
                  type="button"
                  onClick={() => toggle(ns)}
                  className="w-full flex items-center justify-between px-2 py-1.5 rounded-md text-muted-foreground dark:text-[#8a8f98] uppercase tracking-[0.06em] hover:bg-muted/50 dark:hover:bg-white/[0.04] transition-colors duration-150"
                  style={{ fontSize: '10px', fontWeight: 590 }}
                >
                  {NAMESPACE_LABELS[ns] || ns}
                  {collapsed[ns] ? (
                    <Plus className="w-3 h-3" weight="bold" />
                  ) : (
                    <Minus className="w-3 h-3" weight="bold" />
                  )}
                </button>
                {!collapsed[ns] && vars.map(v => (
                  <button
                    key={v.token}
                    type="button"
                    onClick={() => { onInsert(v.token); setOpen(false) }}
                    className="w-full text-left px-2 py-1.5 rounded-md hover:bg-[#5e6ad2]/[0.08] dark:hover:bg-[#5e6ad2]/[0.12] flex items-center justify-between gap-3 transition-colors duration-150"
                  >
                    <span
                      className="text-foreground dark:text-[#f7f8f8] truncate"
                      style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                    >
                      {v.label}
                    </span>
                    <span
                      className="text-muted-foreground dark:text-[#62666d] font-mono truncate flex-shrink-0"
                      style={{ fontSize: '10px', letterSpacing: '-0.05px' }}
                    >
                      {v.token}
                    </span>
                  </button>
                ))}
              </div>
            ))
          ) : (
            <div className="px-3 py-4 text-center" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
              <TextShimmer>Loading…</TextShimmer>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
